/**
 * Pure diff utilities — compare a frozen server snapshot against the live working
 * tree and classify each entity. Consumed by the changeset builder, the tree
 * color coding, and the Save All counter.
 *
 * Status values:
 *   - "pristine": identical to server, no local change
 *   - "modified": exists on server, at least one field differs locally
 *   - "created":  local-only entity (its public_id is not on the server)
 *   - "deleted":  server entity marked localDeleted (still visible, barred in UI)
 */

const DEVICE_DIRTY_FIELDS = [
  "name",
  "brand",
  "transport",
  "host",
  "rtsp_port",
  "api_port",
  "rtsp_port_redirected",
  "api_port_redirected",
  "username",
  "password",
  "caching",
  "set_date",
];

const CAMERA_DIRTY_FIELDS = [
  "slot_number",
  "stream_quality",
  "url_mode",
  "rtsp_url_suffix",
  "protocol",
  "inference_machine",
  "alert_channel",
  "depth_of_field",
  "is_active",
];

const VIEW_DIRTY_FIELDS = [
  "view_number",
  "view_name",
  "view_area",
  "resolution",
  "body_sensitivity",
  "bag_sensitivity",
  "relevance",
  "is_active",
  "body_theft",
  "gesture_into_body",
  "product_in_personal_bag",
  "product_into_backpack",
  "suspicious_bag",
  "product_into_stroller",
  "client_normal_bag",
  "store_normal_bag",
  "deblistering",
  "consumption",
  "burst_shot",
  "other_suspicious",
];

// Foreign-key fields that may arrive either as integer ids or as nested
// {id, …} objects depending on serializer choices. Normalised before equality.
const DEVICE_FK_FIELDS = new Set(["brand"]);
const CAMERA_FK_FIELDS = new Set(["inference_machine", "alert_channel", "depth_of_field"]);
const VIEW_FK_FIELDS = new Set(["body_sensitivity", "bag_sensitivity", "relevance"]);

function fkValue(field) {
  if (field === null || field === undefined) return null;
  if (typeof field === "object") return field.id ?? null;
  return field;
}

// A view's frame size, compared and sent as exactly `{width, height}`. The API
// reads it back in that shape, but key order or extra keys (`id`) must not turn
// an untouched frame into a diff. Anything else (null, a bare PK) passes through.
function normalizeResolution(value) {
  if (value && typeof value === "object" && Number.isFinite(value.width) && Number.isFinite(value.height)) {
    return { width: value.width, height: value.height };
  }
  return value ?? null;
}

const RESOLUTION_FIELDS = new Set(["resolution"]);

function normalizedEqual(a, b, isFk, isResolution = false) {
  let left = a;
  let right = b;
  if (isFk) {
    left = fkValue(a);
    right = fkValue(b);
  }
  if (isResolution) {
    left = normalizeResolution(a);
    right = normalizeResolution(b);
  }
  if (left === right) return true;
  if (left == null && right == null) return true;
  // Treat empty string and null/undefined as equivalent for optional text fields.
  if ((left === "" && right == null) || (right === "" && left == null)) return true;
  // For composite values (arrays like view_area, JSON objects), compare by serialized value
  // since two deep-clones of the same source share content but not reference identity.
  if (typeof left === "object" && typeof right === "object") {
    return JSON.stringify(left) === JSON.stringify(right);
  }
  return false;
}

function isDirty(serverEntity, workingEntity, fields, fkFields) {
  return fields.some(
    (f) => !normalizedEqual(serverEntity[f], workingEntity[f], fkFields.has(f), RESOLUTION_FIELDS.has(f))
  );
}

/**
 * Walks the working tree side-by-side with the server tree and assigns a
 * status to every entity it encounters. Returns a Map keyed by public_id.
 */
export function computeStatusMap(serverTree, workingTree) {
  const serverDevices = new Map();
  const serverCameras = new Map();
  const serverViews = new Map();
  (serverTree || []).forEach((d) => {
    serverDevices.set(d.public_id, d);
    (d.cameras || []).forEach((c) => {
      serverCameras.set(c.public_id, c);
      (c.views || []).forEach((v) => serverViews.set(v.public_id, v));
    });
  });

  const status = new Map();
  const classify = (entity, serverMap, dirtyFields, fkFields) => {
    if (entity.localDeleted) {
      status.set(entity.public_id, "deleted");
      return;
    }
    const server = serverMap.get(entity.public_id);
    if (!server) {
      status.set(entity.public_id, "created");
      return;
    }
    if (isDirty(server, entity, dirtyFields, fkFields)) {
      status.set(entity.public_id, "modified");
      return;
    }
    status.set(entity.public_id, "pristine");
  };

  (workingTree || []).forEach((device) => {
    classify(device, serverDevices, DEVICE_DIRTY_FIELDS, DEVICE_FK_FIELDS);
    (device.cameras || []).forEach((camera) => {
      classify(camera, serverCameras, CAMERA_DIRTY_FIELDS, CAMERA_FK_FIELDS);
      (camera.views || []).forEach((view) => {
        classify(view, serverViews, VIEW_DIRTY_FIELDS, VIEW_FK_FIELDS);
      });
    });
  });

  return status;
}

/**
 * Count of non-pristine descendants of a given node (a device or a camera).
 * Used to render a small "n changes inside" badge on parent rows whose own
 * status is pristine.
 */
export function rollupCount(statusMap, node) {
  let n = 0;
  (node.cameras || []).forEach((camera) => {
    if ((statusMap.get(camera.public_id) || "pristine") !== "pristine") n += 1;
    (camera.views || []).forEach((view) => {
      if ((statusMap.get(view.public_id) || "pristine") !== "pristine") n += 1;
    });
  });
  return n;
}

/**
 * Tree-wide totals for the Save All bar.
 */
export function summarizeStatus(statusMap) {
  let modified = 0;
  let created = 0;
  let deleted = 0;
  statusMap.forEach((s) => {
    if (s === "modified") modified += 1;
    else if (s === "created") created += 1;
    else if (s === "deleted") deleted += 1;
  });
  return { modified, created, deleted, total: modified + created + deleted };
}

export const diffInternals = {
  DEVICE_DIRTY_FIELDS,
  CAMERA_DIRTY_FIELDS,
  VIEW_DIRTY_FIELDS,
  DEVICE_FK_FIELDS,
  CAMERA_FK_FIELDS,
  VIEW_FK_FIELDS,
  RESOLUTION_FIELDS,
  fkValue,
  normalizedEqual,
  normalizeResolution,
};
